"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="222580" className="flex gap-6">
        <div className="h-5 w-16 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default text-sm md:text-xs uppercase tracking-widest text-foreground hover:text-primary transition-colors relative group">
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-chart-1 transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        <div className="h-8 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-12">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="font-default text-lg md:text-base uppercase tracking-widest text-foreground block py-2 border-b border-primary/10 hover:text-chart-1 transition-colors">
            {item.label}
          </Link>
        </div>)}
      <div className="mt-6">
        <Button asChild className="w-full rounded-lg bg-secondary text-primary-foreground hover:bg-chart-1/90 font-default tracking-wide py-6 text-base md:text-sm shadow-sm">
          <Link to="https://hello.dubsado.com:443/public/appointment-scheduler/662a79049b3568003af78fe0/schedule">Book Consultation</Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className="sticky top-0 z-50 w-full transition-all duration-300 border-b border-primary/10 bg-primary">
      <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex-shrink-0">
          <img src="https://sunny-rooster.10web.cloud/wp-content/uploads/2026/05/Birthing-Joy-Logo.png" alt="Birthing Joy" className="!h-auto text-primary ![margin:70px_0px_0px_0px] rounded-tl-[100px] rounded-tr-[100px] rounded-br-[100px] rounded-bl-[100px] !w-[150px]" />
        </Link>

        <div className="hidden lg:flex items-center gap-8 lg:gap-12">
          <MenuProvider menu_id="16">
            <DesktopMenu />
          </MenuProvider>
          <Button asChild className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-9 py-2 has-[>svg]:px-3 rounded-full font-default tracking-wide px-6 shadow-md transition-all duration-300 animate-pulse text-primary disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-chart-1/90 hover:animate-none bg-secondary">
            <Link to="https://hello.dubsado.com:443/public/appointment-scheduler/662a79049b3568003af78fe0/schedule">Book Consultation</Link>
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <Button asChild size="sm" className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-8 gap-1.5 px-3 has-[>svg]:px-2.5 rounded-full font-default tracking-wide shadow-sm text-primary !bg-[#EDB9B9] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-chart-1/90">
            <Link to="https://hello.dubsado.com:443/public/appointment-scheduler/662a79049b3568003af78fe0/schedule">Consult</Link>
          </Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-primary/5">
                <Menu className="size-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-[300px] bg-background border-l border-primary/10 ![padding:0px_10px_0px_10px] sm:max-w-sm sm:w-[400px]">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <MenuProvider menu_id="16">
                <MobileMenu />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}